import {
  format,
  formatDistanceToNowStrict,
  isSameYear,
  isValid,
  parseISO,
  startOfMonth,
} from 'date-fns';
import { EMPTY_VALUE } from '@/constants/app.constants';

export function formatDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function isOutsideDateLimits(
  date: Date,
  minIsoDate: string | undefined,
  maxIsoDate: string | undefined,
): boolean {
  const day = formatDate(date);
  if (minIsoDate && day < minIsoDate) return true;
  if (maxIsoDate && day > maxIsoDate) return true;
  return false;
}

export type DatePeriod = Record<'dateFrom' | 'dateTo', string>;

export function getMonthToDateRange(): DatePeriod {
  const today = new Date();
  return {
    dateFrom: formatDate(startOfMonth(today)),
    dateTo: formatDate(today),
  };
}

const DAY_MONTH_FORMAT = 'dd MMM';
const DAY_MONTH_YEAR_FORMAT = 'dd MMM yyyy';
const OPEN_RANGE_EDGE_TEXT = '…';

const MONTH_NAME_FORMAT = 'MMMM';
const MONTHS_IN_YEAR = 12;

interface NumberOption {
  id: number;
  name: string;
}

export function getMonthOptions(): NumberOption[] {
  const thisYear = new Date().getFullYear();
  return Array.from({ length: MONTHS_IN_YEAR }, (_, monthIndex) => ({
    id: monthIndex + 1,
    name: format(new Date(thisYear, monthIndex, 1), MONTH_NAME_FORMAT),
  }));
}

export function getYearOptions(firstYear: number, lastYear: number): NumberOption[] {
  return Array.from({ length: lastYear - firstYear + 1 }, (_, offset) => {
    const year = lastYear - offset;
    return { id: year, name: String(year) };
  });
}

function formatRangeEdgeWithYear(isoDate: string): string {
  return isoDate ? format(parseISO(isoDate), DAY_MONTH_YEAR_FORMAT) : OPEN_RANGE_EDGE_TEXT;
}

export function formatDateRangeWithYear(startIsoDate: string, endIsoDate: string): string {
  if (startIsoDate === endIsoDate) return formatRangeEdgeWithYear(startIsoDate);
  const isOneYear =
    Boolean(startIsoDate) &&
    Boolean(endIsoDate) &&
    isSameYear(parseISO(startIsoDate), parseISO(endIsoDate));
  const startText = isOneYear
    ? format(parseISO(startIsoDate), DAY_MONTH_FORMAT)
    : formatRangeEdgeWithYear(startIsoDate);
  return `${startText} – ${formatRangeEdgeWithYear(endIsoDate)}`;
}

const WEEKDAY_FORMAT = 'EEEE';

export function formatWeekday(isoDate: string): string {
  const date = parseISO(isoDate);
  return isValid(date) ? format(date, WEEKDAY_FORMAT) : EMPTY_VALUE;
}

export function formatDateTimeWithSeconds(isoString: string | null): string {
  if (!isoString) return EMPTY_VALUE;
  const date = parseISO(isoString);
  if (!isValid(date)) return EMPTY_VALUE;
  return format(date, 'M/d/yyyy h:mm:ss a');
}

export function formatTime(isoString: string | null): string {
  if (!isoString) return EMPTY_VALUE;
  const date = parseISO(isoString);
  if (!isValid(date)) return EMPTY_VALUE;
  return format(date, 'hh:mm a');
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatDisplayDate(value: string | Date | null | undefined): string {
  if (!value) return EMPTY_VALUE;
  let date: Date;
  if (typeof value === 'string') {
    const match = DATE_ONLY_RE.exec(value);
    date = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : parseISO(value);
  } else {
    date = value;
  }
  if (!isValid(date)) return EMPTY_VALUE;
  return format(date, 'dd MMM yyyy');
}

export function formatRelativeTime(value: string | Date | null | undefined): string {
  if (!value) return EMPTY_VALUE;
  const date = typeof value === 'string' ? parseISO(value) : value;
  if (!isValid(date)) return EMPTY_VALUE;
  return formatDistanceToNowStrict(date, { addSuffix: true });
}

export function formatDisplayDateTime(value: string | Date | null | undefined): string {
  if (!value) return EMPTY_VALUE;
  const date = typeof value === 'string' ? parseISO(value) : value;
  if (!isValid(date)) return EMPTY_VALUE;
  return format(date, "dd MMM yyyy, hh:mm' 'a");
}
